import { Trans, t } from "@lingui/macro";
import Link from "next/link";

type FaqDetailsProps = {
  key: string;
  searchTitle: string;
  title: any;
  answer: any;
};

export const faqDetails: FaqDetailsProps[][] = [
  [
    {
      key: "general",
      searchTitle: t`What is Lumi?`,
      title: <Trans>What is Lumi?</Trans>,
      answer: (
        <Trans>
          Lumi, or Lumi News, is a Malaysia-based news aggregator app with more
          than 50+ trusted publishers. It is available in English, Bahasa
          Melayu, and Chinese. Use Lumi for an easier and faster way to read
          news and share it.
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`Is Lumi free to use?`,
      title: <Trans>Is Lumi free to use?</Trans>,
      answer: (
        <Trans>
          <p>Yes, Lumi is free to use!</p>
          <p>
            The latest version of the Lumi News app can be downloaded free on
            iOS and Android devices via the App Store, Google Play Store, and
            Huawei AppGallery.
          </p>
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`Are the articles displayed on the Lumi app and website written by Lumi?`,
      title: (
        <Trans>
          Are the articles displayed on the Lumi app and website written by
          Lumi?
        </Trans>
      ),
      answer: (
        <Trans>
          As a news aggregation app, Lumi automatically gathers articles from
          reputable sources from our network of publishers. We do not write or
          replicate any publisher's content ourselves. To find out who wrote the
          article you're reading, look for the publisher's logo and author's
          name.
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`Who is responsible for the content that appears on Lumi?`,
      title: (
        <Trans>Who is responsible for the content that appears on Lumi?</Trans>
      ),
      answer: (
        <Trans>
          The content that appears on our app is the sole responsibility of the
          original source from which it was obtained. We are not responsible for
          the accuracy, reliability, or completeness of the content. However, we
          do our best to select sources that are reputable and reliable. We want
          Lumi to be the centre for news you can trust!
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`Why are there ads on Lumi?`,
      title: <Trans>Why are there ads on Lumi?</Trans>,
      answer: (
        <Trans>
          There are no ads on Lumi. However, publishers on Lumi may show you ads
          when you read their articles via the Lumi app. Lumi has no control
          over ads that are displayed by a publisher in their articles. You may
          review a publisher's privacy policies should you have any concerns
          about the ads you're seeing on their site.
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`How do I stop ads from popping up in an article?`,
      title: <Trans>How do I stop ads from popping up in an article?</Trans>,
      answer: (
        <Trans>
          Lumi is free to use and we do not serve any ads. Ads on articles are
          from the publisher's website.
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`What is Reader Mode?`,
      title: <Trans>What is Reader Mode?</Trans>,
      answer: (
        <Trans>
          Reader Mode is a feature on the Lumi News app that enables you to read
          articles from selected publishers in a focused manner. Articles will
          also load faster without ads. Make Reader Mode your default setting
          under Profile &gt; Display &gt; Reader Mode
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`Why is Reader Mode only available for selected publishers or articles?`,
      title: (
        <Trans>
          Why is Reader Mode only available for selected publishers or articles?
        </Trans>
      ),
      answer: (
        <Trans>
          Reader Mode is a feature we're improving over time. Not all articles
          or publishers may be compatible with the current capabilities of
          Reader Mode. You may contact us to inquire about the possibility of
          adding Reader Mode support for a specific publisher or article.
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`Why am I not seeing the latest news?`,
      title: <Trans>Why am I not seeing the latest news?</Trans>,
      answer: (
        <Trans>
          <ul className="list-disc pl-4">
            <li>
              Ensure that you have good network coverage on your mobile phone.
            </li>
            <li>
              Drag or pull down to refresh and load the latest news on your
              feed.
            </li>
            <li>
              If there are no newer articles even after refreshing, the
              publishers you're currently following may not have published
              anything new since your last visit.
            </li>
          </ul>
          <br />
          <p>
            Tip: Follow more publishers to see more content you'll like from
            different sources.
          </p>
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`I see an 'Uh Oh' screen. What's happening?`,
      title: <Trans>I see an 'Uh Oh' screen. What's happening?</Trans>,
      answer: (
        <Trans>
          <p>You might have run into an unusual error.</p>
          <br />
          <ul className="list-disc pl-4">
            <li>
              Ensure you have steady network coverage on your mobile phone.
            </li>
            <li>
              Check that you have the latest version of the Lumi News app on App
              Store or Play Store.
            </li>
            <li>Force quit your app and relaunch.</li>
            <li>Restart your phone to see if the issue is fixed.</li>
            <li>Try logging again or re-installing the app.</li>
          </ul>
          <br />
          <p>
            If the issue persists, please let us know by using the "Contact Us"
            button.
          </p>
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`What is a News Stack?`,
      title: <Trans>What is a News Stack?</Trans>,
      answer: (
        <Trans>
          A News Stack is a compilation of all the must-read updates about
          today's hottest topic, selected by Lumi's editorial team. View the
          biggest headlines about that topic, all in one convenient, shareable
          feed.
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`Why do some News Stacks have an 'exclusive' badge?`,
      title: <Trans>Why do some News Stacks have an 'exclusive' badge?</Trans>,
      answer: (
        <Trans>
          The 'exclusive' badge signifies content that is exclusively available
          on the Lumi News app. Lumi may collaborate with reputable
          personalities or brands to provide unique and informative content.
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`What does it mean to 'Allow tracking' on the app?`,
      title: <Trans>What does it mean to 'Allow tracking' on the app?</Trans>,
      answer: (
        <Trans>
          When you 'Allow tracking' on the app, you're letting us provide a
          customised experience tailored to your interest and provide you with
          personalised advertising.
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`I’m having an issue. What should I do?`,
      title: <Trans>I’m having an issue. What should I do?</Trans>,
      answer: (
        <Trans>
          Let us know and we will fix it! On the app, Go to Profile &gt; Info
          &gt; Contact us and get in touch with us. You can also email us at
          help@luminews.my or leave us a message on Facebook/Instagram/Twitter.
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`Does Lumi have a Home screen widget?`,
      title: <Trans>Does Lumi have a Home screen widget?</Trans>,
      answer: (
        <Trans>
          Yes, Lumi has a home screen widget. Learn more on how to add our
          widget to your home screen{" "}
          <a
            className="text-primary2 underline"
            href={t`/news-stack/lumi_news_app_widget`}
            ref={(el) => {
              el.href = t`/news-stack/lumi_news_app_widget`;
            }}
          >
            here
          </a>{" "}
          Additionally, iOS users may also add widgets to your lock screen.
          <a
            className="text-primary2 underline"
            href={t`/news-stack/lumi_news_app_widget#ios-setup`}
            ref={(el) => {
              el.href = t`/news-stack/lumi_news_app_widget#ios-setup`;
            }}
          >
            Learn more
          </a>
        </Trans>
      ),
    },
    {
      key: "general",
      searchTitle: t`How do I report a message?`,
      title: <Trans>How do I report a message?</Trans>,
      answer: (
        <Trans>
          If you encounter behaviour that violates our Community Guidelines or
          see someone who is being harassed or discriminated against, please
          report it by tapping on the message.
        </Trans>
      ),
    },
  ],
  [
    {
      key: "account-preferences",
      searchTitle: t`What's the difference between a Guest and logged in user?`,
      title: (
        <Trans>What's the difference between a Guest and logged in user?</Trans>
      ),
      answer: (
        <Trans>
          For now, logged-in users and guests can use all the features on the
          app. However, as a logged-in user, we'll remember your settings and
          app preferences whenever you're using Lumi from another device and
          even when you reinstall the app. It's more convenient!
        </Trans>
      ),
    },
    {
      key: "account-preferences",
      searchTitle: t`I’m a new user to Lumi. How do I sign up / login?`,
      title: <Trans>I’m a new user to Lumi. How do I sign up / login?</Trans>,
      answer: (
        <Trans>
          To create a Lumi account, simply connect Lumi to your existing Google,
          Facebook, or Apple ID account. <br />
          If you're already using Lumi News as a Guest without creating an
          account, you may go to Profile &gt; Settings to create an account. All
          your settings will be saved immediately to your newly created account.
        </Trans>
      ),
    },
    {
      key: "account-preferences",
      searchTitle: t`Why did I get logged out automatically?`,
      title: <Trans>Why did I get logged out automatically?</Trans>,
      answer: (
        <Trans>
          Oops. It is unusual for users to get logged out automatically from the
          Lumi News app. Should this happen, please get in touch with the Lumi
          team on our social media channels or send an email to:
          help@luminews.my.
        </Trans>
      ),
    },
    {
      key: "account-preferences",
      searchTitle: t`Why am I seeing news from other languages or other publishers I don't follow?`,
      title: (
        <Trans>
          Why am I seeing news from other languages or other publishers I don't
          follow?
        </Trans>
      ),
      answer: (
        <Trans>
          Articles you see on your feed are based on your account settings. You
          should only see articles from publishers that you follow. To follow or
          unfollow publishers based on your liking, go to Profile &gt; &nbsp;
          Publishers to view the list of publishers you currently follow. Click
          on the language tab (Bahasa Melayu, English, or Chinese) to see
          Publishers in other languages.
        </Trans>
      ),
    },
    {
      key: "account-preferences",
      searchTitle: t`Can I change the article's text size? `,
      title: <Trans>Can I change the article's text size?</Trans>,
      answer: (
        <Trans>
          All articles on Lumi News are displayed according to the publisher's
          website.
          <br /> <br />
          However, you may increase or decrease the text size on your device
          according to your preference. The easiest way to make the font bigger
          or smaller is by changing it in your Phone Settings under
          Accessibility options.
          <br />
          <br />
          For iOS devices, go to Settings &gt; Display & Brightness &gt; Text
          Size
          <br />
          For Android devices, go to Settings &gt; Display &gt; Text Size
        </Trans>
      ),
    },
    {
      key: "account-preferences",
      searchTitle: t`I clicked into a News Stack but there were little to no articles. Why?`,
      title: (
        <Trans>
          I clicked into a News Stack but there were little to no articles. Why?
        </Trans>
      ),
      answer: (
        <Trans>
          <p>
            Each News Stack is carefully curated by Lumi's in-house editorial
            team, and the number of articles in a News Stack may differ from one
            to another.
          </p>
          <p>You'll see articles in a News Stack if:</p>
          <ul className="list-decimal pl-4">
            <li>
              The article in the News Stack is published by a publisher you're
              currently following on Lumi.
            </li>
            <li>
              The article in the News Stack is by a publisher you may not
              follow, but in the same app language you've set.
            </li>
          </ul>
          <p>
            If you don't see any articles at all, it means there are no articles
            currently on the topic from publishers based on your chosen
            language.
          </p>
          <p>
            Tip: Follow more publishers to see more content from different
            sources! It'll make your feed more 'happening'.
          </p>
        </Trans>
      ),
    },
    {
      key: "account-preferences",
      searchTitle: t`I'm on Dark Mode but the articles are not. Can I change this?`,
      title: (
        <Trans>
          I'm on Dark Mode but the articles are not. Can I change this?
        </Trans>
      ),
      answer: (
        <Trans>
          An article is displayed according to the publisher's website. Due to
          some limitations, the dark mode cannot be applied to certain
          publishers' article pages.
        </Trans>
      ),
    },
    {
      key: "account-preferences",
      searchTitle: t`The Share Icon disappears when viewing an article.`,
      title: <Trans>The Share Icon disappears when viewing an article.</Trans>,
      answer: (
        <Trans>
          If the Share Icon disappears when viewing an article, it means that
          you've navigated to a page that is not on Lumi's database. You can
          still view the article, but you won't be able to use Lumi's Share
          features.
        </Trans>
      ),
    },
    {
      key: "account-preferences",
      searchTitle: t`How do I delete my account?`,
      title: <Trans>How do I delete my account?</Trans>,
      answer: (
        <Trans>
          <p>Important note before deleting your Lumi News account:</p>
          <br />
          <ul className="list-disc pl-4">
            <li>
              Your account along with all its settings and data will be deleted.
            </li>
            <li>
              Once your account has been deleted, you will no longer be able to
              log into your account on the Lumi News app with the same phone
              number again.
            </li>
          </ul>
          <br />
          <p>
            If you're agreeable to the terms above, send in your request to
            delete your account via the 'Contact us' button in your Profile of
            the app, or send an email to help@luminews.my. Remember to include
            the phone number you signed up with.
          </p>
          <br />
          <p>
            We will attend to your request within 24 hours on weekdays between
            9am-7pm, Malaysian time.
          </p>
        </Trans>
      ),
    },
    {
      key: "account-preferences",
      searchTitle: t`If I delete my account, can I create a new account with the same phone number?`,
      title: (
        <Trans>
          If I delete my account, can I create a new account with the same phone
          number?
        </Trans>
      ),
      answer: (
        <Trans>
          Yes. However, you will have to start again with the new account. All
          your previous settings and data cannot be retrieved nor transferred to
          your new account.
        </Trans>
      ),
    },
  ],
  [
    {
      key: "user-profiles",
      searchTitle: t`How to create a username on Lumi?`,
      title: <Trans>How to create a username on Lumi?</Trans>,
      answer: (
        <Trans>
          Go to Profile &gt; Edit profile <br />
          <br />
          Username guide lines: <br />* Your username must be unique and hasn't
          been taken by another user. <br />* Must be between six and 20
          characters long. <br />* Can contain any letters from a to z and any
          numbers from 0 through 9. <br />* Can contain . (period) or/and _
          (underscore). <br />
          Is not case-sensitive. <br />
          <br />
          You can only change your username once within 60 days.
        </Trans>
      ),
    },
    {
      key: "user-profiles",
      searchTitle: t`How do I change my display name on Lumi?`,
      title: <Trans>How do I change my display name on Lumi?</Trans>,
      answer: (
        <Trans>
          Go to Profile &gt; Edit profile
          <br />
          <br />
          Real names are encouraged so others can recognise you.
        </Trans>
      ),
    },
    {
      key: "user-profiles",
      searchTitle: t`How do I change my Lumi character?`,
      title: <Trans>How do I change my Lumi character?</Trans>,
      answer: (
        <Trans>
          Go to Profile &gt; Edit profile &gt; Change profile photo
          <br />
          <br />
          Select your Lumi character from the options provided.
        </Trans>
      ),
    },
    {
      key: "user-profiles",
      searchTitle: t`How do I change my Lumi bio?`,
      title: <Trans>How do I change my Lumi bio?</Trans>,
      answer: (
        <Trans>
          Go to Profile &gt; Edit profile
          <br />
          <br />
          Tell others about your work, things you're interested in, or how you
          want people to remember you
        </Trans>
      ),
    },
  ],
  [
    {
      key: "notification",
      searchTitle: t`Why am I not receiving any notifications?`,
      title: <Trans>Why am I not receiving any notifications?</Trans>,
      answer: (
        <Trans>
          <ul className="list-disc pl-4">
            <li>Log out and log in again.</li>
            <li>Check if your push notifications have been set correctly.</li>
            <li>
              Turn on notifications on the Lumi News app to get updates based on
              topics and publishers you follow.
            </li>
          </ul>
          <br />
          <ol className="list-decimal pl-4">
            <li>Go to Account on your Lumi News app</li>
            <li>
              Tap on Settings &gt; Notifications &gt; General Notifications
            </li>
            <li>Choose which option you prefer</li>
          </ol>
          <br />
          <ul className="list-disc pl-4">
            <li>
              Ensure your phone settings allow you to receive Lumi News
              notifications. Give Lumi permission in your Phone Settings.
            </li>
          </ul>
          <br />
          <p>For iOS users:</p>
          <ol className="list-decimal pl-4">
            <li>Open your device Settings</li>
            <li>Scroll down and select Lumi News</li>
            <li>
              Select Notifications and ensure that "Allow Notifications" is
              checked
            </li>
          </ol>
          <br />
          <p>For Android users:</p>
          <ol className="list-decimal pl-4">
            <li>Go to Settings &gt; Apps &gt; Lumi News </li>
            <li>Under the app settings, find Permissions &gt; Notifications</li>
            <li>Ensure that Show notification is toggled on </li>
          </ol>
          <br />
          <p>
            <ul className="list-disc pl-4">
              <li>
                Make sure you're not blocking notifications on your device.
              </li>
            </ul>
            <br />
            For iOS, check your Settings &gt; Focus as the Focus can temporarily
            silence all notifications.
            <br />
            For Android users, check your Sound & vibration &gt; Do Not Disturb
            (DND) settings, as DND can temporarily silence all notifications.
          </p>
        </Trans>
      ),
    },
    {
      key: "notification",
      searchTitle: t`How often will I receive notifications from Lumi?`,
      title: <Trans>How often will I receive notifications from Lumi?</Trans>,
      answer: (
        <Trans>
          <p>You can set your notification preferences to:</p>
          <br />
          <ul className="list-disc pl-4">
            <li>
              When something trends: You’ll receive a notification whenever a
              news gets hot. Frequency is higher than Important news only.
            </li>
            <li>
              Important news only: You’ll receive notifications only for top
              headlines of the day. Less frequent compared to When something
              trends.
            </li>
            <li>
              I want everything: You will a notification for every new article
              according to the topics and publishers you follow.
            </li>
            <li>
              Silent: You will not receive any news notifications, including app
              updates. To customise your notification settings, tap on Settings
              &gt; Notifications &gt; General Notifications and choose which
              option you prefer.
            </li>
          </ul>
        </Trans>
      ),
    },
    {
      key: "notification",
      searchTitle: t`I clicked on a notification previously and I now want to go back to the same story but I can't find it.`,
      title: (
        <Trans>
          I clicked on a notification previously and I now want to go back to
          the same story but I can't find it.
        </Trans>
      ),
      answer: (
        <Trans>
          All notifications that you received in the last 7 days can be found on
          the Notification History page. <br />
          Tap on the bell icon on the top right of your Home page to view the
          list.
        </Trans>
      ),
    },
    {
      key: "notification",
      searchTitle: t`What is the red dot or numbered red badge on the Lumi icon?`,
      title: (
        <Trans>
          What is the red dot or numbered red badge on the Lumi icon?
        </Trans>
      ),
      answer: (
        <Trans>
          The red dot or numbered red badge on the Lumi News app icon indicates
          notifications you have missed from us.
        </Trans>
      ),
    },
    {
      key: "notification",
      searchTitle: t`How do I clear the numbered red badge on the Lumi icon?`,
      title: (
        <Trans>How do I clear the numbered red badge on the Lumi icon?</Trans>
      ),
      answer: (
        <Trans>
          <p>For iOS users:</p>
          <ol className="list-decimal pl-4">
            <li>Open the Lumi News app.</li>
            <li>
              Press on the bell icon (Notification History) on the top right. It
              should clear your notifications!
            </li>
          </ol>
          <br />
          <p>For Android users:</p>
          <ol className="list-decimal pl-4">
            <li>Open the app. </li>
            <li>
              Press on the bell (Notification History) and it should clear your
              notification badge.
            </li>
            <li>
              If that doesn't work, clear your phone/system notifications.
            </li>
          </ol>
        </Trans>
      ),
    },
  ],
  [
    {
      key: "community",
      searchTitle: t`What are Lumi's community guidelines and rules for posting comments or messages in chats?`,
      title: (
        <Trans>
          What are Lumi's community guidelines and rules for posting comments or
          messages in chats?
        </Trans>
      ),
      answer: (
        <Trans>
          We strive to maintain a respectful space for sharing ideas. Your
          cooperation in following the guidelines below and familiarity with it
          is essential.
          <br />
          <br />
          Be kind. Treat others with respect, even if you disagree with them.
          <br />
          Be open-minded. Be willing to listen and learn from people with
          different perspectives.
          <br />
          Be inclusive. Avoid using language that is offensive or
          discriminatory.
          <br />
          Be focused. When participating in discussions, please stay on topic
          and avoid derailing the conversation with irrelevant commentary.
          <br />
          Be discerning. Check the facts and cite your sources to ensure that
          you are spreading accurate information and avoiding misinformation.
          <br />
          No ads. We want to keep Lumi News a distraction-free zone, so refrain
          from posting ads, promotions, or commercial content.
          <br />
          Be helpful. If you encounter behaviour that violates our Community
          Guidelines or see someone who is being harassed or discriminated
          against, please report it by tapping on the message. We take all
          reports seriously and will take appropriate action to maintain a safe
          and respectful community.
          <br />
          Opinions expressed in this app, including comments and chats, are
          solely those of individual authors and users and do not represent the
          official stance nor opinion of Lumi News, its partners, affiliates, or
          employees.
          <br />
          We do not endorse or verify the accuracy of user-generated comments
          and messages. Users are solely responsible for their comments and
          chats. While we strive to maintain a respectful environment, we do not
          assume responsibility for the content created by individual users.
          <br />
          We reserve the right to disable users and remove any content that
          violates our Community Guidelines, including content that is harmful,
          unsafe, or illegal.
        </Trans>
      ),
    },
    {
      key: "community",
      searchTitle: t`How can I chat or leave a comment?`,
      title: <Trans>How can I chat or leave a comment?</Trans>,
      answer: (
        <Trans>
          To chat or leave a comment on Lumi, you need to log in to your
          account. Chat rooms and the comment section are accessible to
          registered users via the Explore tab (chat) and in articles and News
          Stacks (comments). Guest users are unable to send messages or leave
          comments.
        </Trans>
      ),
    },
    {
      key: "community",
      searchTitle: t`How do I report a comment or message?`,
      title: <Trans>How do I report a comment or message?</Trans>,
      answer: (
        <Trans>
          Opinions expressed in this app, including comments and chats, are
          solely those of individual authors and users and do not represent the
          official stance nor opinion of Lumi News, its partners, affiliates, or
          employees.
          <br />
          <br />
          If you encounter behaviour that violates our Community Guidelines or
          see someone who is being harassed or discriminated against, please
          report it by tapping on the message. We take all reports seriously and
          will take appropriate action to maintain a safe and respectful
          community.
        </Trans>
      ),
    },
    {
      key: "community",
      searchTitle: t`My account has been banned or restricted from certain actions, including commenting or chatting. What can I do?`,
      title: (
        <Trans>
          My account has been banned or restricted from certain actions,
          including commenting or chatting. What can I do?
        </Trans>
      ),
      answer: (
        <Trans>
          If you find yourself in this situation, it's possible that a user
          reported your account. We reserve the right to disable users and
          remove any content that violates our Community Guidelines, including
          content that is harmful, unsafe, or illegal.
          <br />
          <br />A decision may be appealed. You may submit a formal request by
          sending an email to help@luminews.my, where the decision may be
          reviewed again to determine whether or not it follows our Community
          Guidelines. The process may take up to two weeks.
        </Trans>
      ),
    },
    {
      key: "community",
      searchTitle: t`Am I liable for the comments and messages that I post on Lumi?`,
      title: (
        <Trans>
          Am I liable for the comments and messages that I post on Lumi?
        </Trans>
      ),
      answer: (
        <Trans>
          Opinions expressed in this app, including comments and chats, are
          solely those of individual authors and users and do not represent the
          official stance nor opinion of Lumi News, its partners, affiliates, or
          employees.
          <br />
          We do not endorse or verify the accuracy of user-generated comments
          and messages. Users are solely responsible for their comments and
          chats. While we strive to maintain a respectful environment, we do not
          assume responsibility for the content created by individual users.
        </Trans>
      ),
    },
    {
      key: "community",
      searchTitle: t`I can't find my message in a chat or comment section. What happened?`,
      title: (
        <Trans>
          I can't find my message in a chat or comment section. What happened?
        </Trans>
      ),
      answer: (
        <Trans>
          Can't find your message? Here are possible reasons:
          <br />
          <br /> • If your message in chat or comments is not visible, it may
          have been removed for not complying with our Community Guidelines.
          These guidelines are in place to maintain a safe and welcoming
          environment for all users of the Lumi News app.
          <br /> • Another user may have reported your message, leading to its
          removal. We take user reports seriously to maintain a positive user
          experience.
          <br />• If your internet connection was unstable when you sent your
          message, it may not have been sent properly. In this case, you can try
          resending your message.{" "}
        </Trans>
      ),
    },
    {
      key: "community",
      searchTitle: t`What should I do if I come across a message or comment I don't like?

    `,
      title: (
        <Trans>
          What should I do if I come across a message or comment I don't like?
        </Trans>
      ),
      answer: (
        <Trans>
          If you encounter a message or comment that violates our Terms of Use
          or Community Guidelines, please report it by tapping on the message or
          comment itself.
          <br />
          <br />
          If the message or comment is making you feel unsafe or is causing you
          emotional distress, it is important to reach out to a trusted friend,
          family member, or mental health professional for support.
          <br />
          <br />
          Opinions expressed in this app, including comments and chats, are
          solely those of individual authors and users and do not represent the
          official stance nor opinion of Lumi News, its partners, affiliates, or
          employees.
          <br />
          We do not endorse or verify the accuracy of user-generated comments
          and messages. Users are solely responsible for their comments and
          chats. While we strive to maintain a respectful environment, we do not
          assume responsibility for the content created by individual users.
        </Trans>
      ),
    },
    {
      key: "community",
      searchTitle: t`Someone is impersonating me, my company, or misusing my trademarked brand. What can I do?`,
      title: (
        <Trans>
          Someone is impersonating me, my company, or misusing my trademarked
          brand. What can I do?
        </Trans>
      ),
      answer: (
        <Trans>
          To address such issues, individuals, companies, and businesses are
          encouraged to report such accounts directly on the app, or submit a
          complaint by using the "Contact Us" button.
        </Trans>
      ),
    },
    {
      key: "community",
      searchTitle: t`I represent a brand, person, or company mentioned in the news, and I have concerns about discussions in the comments or chats. What can I do?`,
      title: (
        <Trans>
          I represent a brand, person, or company mentioned in the news, and I
          have concerns about discussions in the comments or chats. What can I
          do?
        </Trans>
      ),
      answer: (
        <Trans>
          It is important to note that Lumi News has a diverse user base and we
          actively encourage a broad range of viewpoints and ideas, provided
          they do not violate the platform's policies.
          <br />
          <br />
          If you have specific concerns about certain messages or comments, we
          recommend the following steps:
          <br />
          <br />
          Engage constructively: If you believe there's misinformation or have
          concerns about how your brand or company is discussed, consider
          engaging in a constructive conversation. This can help provide clarity
          and address any issues directly.
          <br />
          Report violations: If you come across comments that clearly violate
          our Terms of Use or Community Guidelines, such as those containing
          hate speech or harassment, we encourage you to report them.
          <br />
          Contact support: If you have specific issues related to content
          moderation or need assistance with a particular matter, feel free to
          contact our support team for guidance and assistance.
          <br />
          <br />
        </Trans>
      ),
    },
  ],
  [
    {
      key: "for-you",
      searchTitle: t`What is the 'For You' tab?`,
      title: <Trans>What is the 'For You' tab?</Trans>,
      answer: (
        <Trans>
          The For You tab is your personal feed that shows you content based on
          what you like to do on the Lumi News app, powered by machine learning
          developed by our team. The more you engage with the app, the smarter
          and more accurate your recommendations become over time.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`Why a 'For You' tab?`,
      title: <Trans>Why a 'For You' tab?</Trans>,
      answer: (
        <Trans>
          The For You tab is designed to deliver a personalised news experience
          that caters to your unique interests. This means you'll see more of
          the content you love and less of what you don't.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`How often are my recommendations refreshed?`,
      title: <Trans>How often are my recommendations refreshed?</Trans>,
      answer: (
        <Trans>
          Fresh picks every hour! Your feed updates automatically throughout the
          day, bringing you relevant stories right when you need them.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`How do I personalise my "For You" tab?`,
      title: <Trans>How do I personalise my "For You" tab?</Trans>,
      answer: (
        <Trans>
          You can personalise your 'For You' tab in several ways to enhance your
          experience on Lumi:
          <br />
          • Topic control: Follow topics you're interested in and unfollow those
          you're not. This helps us understand your preferences.
          <br />
          • Interact more: Engage with posts you enjoy - your views help us
          recommend similar content.
          <br />• Fine-tune content: Tap the three dots to select "See more" or
          "See less" option to further refine your recommendations.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`What does 'You've reached the end of your recommendations' mean?`,
      title: (
        <Trans>
          What does 'You've reached the end of your recommendations' mean?
        </Trans>
      ),
      answer: (
        <Trans>
          We refresh your For You feed every hour with content tailored to your
          interests, ensuring you always have access to the latest updates. If
          you see "You've reached the end of your recommendations" at the bottom
          of the tab, check back soon – new suggestions will be available
          shortly with our next refresh.
          <br />
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`How does the 'For You' tab differ from the 'Latest' tab?`,
      title: (
        <Trans>How does the 'For You' tab differ from the 'Latest' tab?</Trans>
      ),
      answer: (
        <Trans>
          For You 🏠 : Your personalised feed for discovering news stories based
          on your preferences, interactions, and interests on Lumi News. This
          feed updates automatically every hour to bring you trending and
          relevant stories tailored just for you throughout the day.
          <br />
          Latest ⚡: Your go-to for breaking news and real-time updates from
          your favourite topics and publishers. This feed updates in real-time
          to ensure you see all the latest new stories the moment they're
          published.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`Can I change the default tab that opens when I launch the app?`,
      title: (
        <Trans>
          Can I change the default tab that opens when I launch the app?
        </Trans>
      ),
      answer: (
        <Trans>
          Yes, you can set 'Latest' or 'For You' as your default tab to open
          automatically when you launch the app.
          <br />
          To do this, go to the dropdown menu and select your preferred tab from
          the options.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`What's "What's Hot This Hour"?`,
      title: <Trans>What's "What's Hot This Hour"?</Trans>,
      answer: (
        <Trans>
          Latest: All trending stories in your language, plus everything hot
          from publishers you follow.
          <br />
          For You: Popular stories that Lumi News readers are actively engaging
          with at this moment.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`Why does my 'Latest' tab look different than before?`,
      title: (
        <Trans>Why does my 'Latest' tab look different than before?</Trans>
      ),
      answer: (
        <Trans>
          We’ve redesigned the 'Latest' tab to improve layout, navigation, and
          content organisation, making it easier and more enjoyable to browse.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`Will the content on the 'Latest' tab remain the same after the redesign?`,
      title: (
        <Trans>
          Will the content on the 'Latest' tab remain the same after the
          redesign?
        </Trans>
      ),
      answer: (
        <Trans>
          Yes, the 'Latest' tab still features the newest articles and updates
          from your followed topics and publishers. The redesign has enhanced
          how you discover and engage with this content.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`Will I still be able to personalise my news feed on the 'Latest' tab?`,
      title: (
        <Trans>
          Will I still be able to personalise my news feed on the 'Latest' tab?
        </Trans>
      ),
      answer: (
        <Trans>
          Yes, you can continue to personalise your news feed on the 'Latest'
          tab by adjusting your preferences, such as the topics and publishers
          you follow.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle:
        't`What do the labels "rising," "interactions," and "featured" mean?`',
      title: (
        <Trans>
          What do the labels "rising," "interactions," and "featured" mean?
        </Trans>
      ),
      answer: (
        <Trans>
          These labels are used to highlight different types of articles based
          on their popularity and engagement:
          <br />
          Rising: News that are gaining popularity and attention quickly.
          <br />
          Interactions: News with high engagement, such as comments, shares, and
          reactions from readers.
          <br />
          Featured: Top news selected by our editors or algorithms, often
          showcasing important or trending news.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`Where is my timeline view?`,
      title: <Trans>Where is my timeline view?</Trans>,
      answer: (
        <Trans>
          We’ve phased out the old timeline view for a more unified experience.
          Recent articles still appear in reverse chronology and are now
          integrated with Shorts, videos, and other content, creating a more
          dynamic and engaging feed.
        </Trans>
      ),
    },
    {
      key: "for-you",
      searchTitle: t`Why am I seeing similar or repeated content in my feed?`,
      title: (
        <Trans>Why am I seeing similar or repeated content in my feed?</Trans>
      ),
      answer: (
        <Trans>
          Different publishers may cover the same topic, but each article can
          offer unique insights, additional information, or differing
          perspectives. This ensures you stay well-informed on trending or
          significant stories from multiple angles.
        </Trans>
      ),
    },
  ],
];
